import React from 'react';
import styled from '@emotion/styled';
import { withTheme } from '@emotion/react';
import { Theme, ThemeProps } from '@theme/base';
import { mixins } from '@styles/mixins';
import { mq } from '@styles/breakpoints';
import { TripAdvisorSubRating } from '@model/iceberg/hotel-content/hotel-content';
import { TestId } from '@components/test-ids';
import { useI18NextContext } from '@components/hooks';

export interface ProductReviewSummaryProps {
  theme?: Theme;
  testId?: string;
  tripAdvisorRating?: number | string;
  tripAdvisorSubRating: TripAdvisorSubRating;
}

const Container = styled.div(({ theme }: ThemeProps) => ({
  ...mixins(theme).colourCard,
  marginBottom: theme.custom.spacing.xLarge,
  ...(theme.custom.typography.paragraph as any),

  ['&:last-of-type']: {
    marginBottom: 0
  }
}));

const Header = styled.div(({ theme }: ThemeProps) => ({
  marginBottom: theme.custom.spacing.medium
}));

const SubHeading = styled.span(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.h4 as any),
  marginRight: theme.custom.spacing.small
}));

const SummaryContainer = styled.div(({ theme }: ThemeProps) => ({
  [mq.small]: {
    display: 'grid',
    gridTemplateColumns: '1.5fr 1fr',
    columnGap: 60,
    marginTop: theme.custom.spacing.medium
  }
}));

const RatingContainer = styled.div(({ theme }: ThemeProps) => ({
  marginBottom: theme.custom.spacing.xLarge,
  [mq.small]: {
    marginBottom: 0
  }
}));

const Rating = styled.div(({ theme }: ThemeProps) => ({
  display: 'flex',
  flexDirection: 'row',
  marginBottom: theme.custom.spacing.medium
}));

const RatingLabel = styled.div({
  minWidth: 90,
  [mq.small]: {
    minWidth: 100
  }
});

interface PercentBarProps extends ThemeProps {
  percent: number;
}
const PercentBar: any = styled.div(({ theme, percent }: PercentBarProps) => ({
  background: `linear-gradient(to right, ${theme.custom.colors.thirdParty.tripAdvisor} ${percent}%, ${theme.custom.colors.group6.dark} ${percent}%);`,
  height: theme.custom.spacing.large,
  marginRight: theme.custom.spacing.small,
  flex: '1 1 auto',
  [mq.small]: {
    flex: '1 0 auto',
    marginRight: theme.custom.spacing.medium,
    width: 160
  },
  [mq.medium]: {
    width: 200
  }
}));

const Count = styled.div(() => ({
  textAlign: 'right',
  width: 40
}));

const Category = styled.div(({ theme }: ThemeProps) => ({
  display: 'flex',
  flexDirection: 'row',
  marginBottom: theme.custom.spacing.medium,
  alignItems: 'center'
}));

const BubbleWrap = styled.div(({ theme }: ThemeProps) => ({
  display: 'flex',
  flexDirection: 'row',
  marginRight: theme.custom.spacing.small
}));

interface RatingBubbleProps extends ThemeProps {
  filled: boolean;
  partial: boolean;
}
const RatingBubble: any = styled.div(({ theme, filled, partial }: RatingBubbleProps) => ({
  background: filled ? theme.custom.colors.thirdParty.tripAdvisor : theme.custom.colors.white,
  borderColor: theme.custom.colors.thirdParty.tripAdvisor,
  borderRadius: '50%',
  borderStyle: 'solid',
  borderWidth: theme.custom.spacing.xxSmall,
  height: theme.custom.spacing.large,
  marginRight: 1,
  width: theme.custom.spacing.large,
  ...(partial && {
    background: `linear-gradient(to right, ${theme.custom.colors.thirdParty.tripAdvisor} 50%, transparent 50%);`
  })
}));

const RatingsMap = [
  '',
  'tripadvisor-rating-one-star',
  'tripadvisor-rating-two-star',
  'tripadvisor-rating-three-star',
  'tripadvisor-rating-four-star',
  'tripadvisor-rating-five-star'
];

export const ProductReviewSummaryComponent = ({
  tripAdvisorRating,
  tripAdvisorSubRating
}: ProductReviewSummaryProps) => {
  const t: any = useI18NextContext();
  const { oneStar, twoStar, threeStar, fourStar, fiveStar, location, cleanliness, service, value } =
    tripAdvisorSubRating;
  const reviewsCount = oneStar + twoStar + threeStar + fourStar + fiveStar;

  if (!reviewsCount) return null;

  const ratingLabelKey = RatingsMap[Math.round(Number(tripAdvisorRating || 0))];
  const renderRating = (labelKey, count, total) => {
    const percent = Math.round((count / total) * 100);

    return (
      <Rating>
        <RatingLabel>{t(labelKey)}</RatingLabel>
        <PercentBar percent={Math.max(1, percent)} />
        <Count>{percent}%</Count>
      </Rating>
    );
  };

  const renderBubbles = (count, rating) => {
    return [...Array(count)].map((_, index: number) => {
      const ratingInt = Math.floor(rating);
      return (
        <RatingBubble key={index} filled={rating >= index + 1} partial={ratingInt !== rating && ratingInt === index} />
      );
    });
  };
  const renderCategory = (labelKey: string, value: number) => {
    return (
      <Category>
        <BubbleWrap>{renderBubbles(5, value)}</BubbleWrap>
        <div>{t(labelKey)}</div>
      </Category>
    );
  };

  return (
    <Container data-testid={TestId.tripAdvisor.subratings}>
      <Header suppressHydrationWarning>
        <SubHeading>
          {tripAdvisorRating && Number(tripAdvisorRating).toFixed(1)} {t(ratingLabelKey)}
        </SubHeading>
        {t('tripadvisor-review-count', { reviews: reviewsCount.toLocaleString() })}
      </Header>
      <SummaryContainer>
        <RatingContainer>
          {renderRating('tripadvisor-rating-five-star', fiveStar, reviewsCount)}
          {renderRating('tripadvisor-rating-four-star', fourStar, reviewsCount)}
          {renderRating('tripadvisor-rating-three-star', threeStar, reviewsCount)}
          {renderRating('tripadvisor-rating-two-star', twoStar, reviewsCount)}
          {renderRating('tripadvisor-rating-one-star', oneStar, reviewsCount)}
        </RatingContainer>
        <div>
          {renderCategory('tripadvisor-location', Number(location))}
          {renderCategory('tripadvisor-cleanliness', Number(cleanliness))}
          {renderCategory('tripadvisor-service', Number(service))}
          {renderCategory('tripadvisor-value', Number(value))}
        </div>
      </SummaryContainer>
    </Container>
  );
};

export const ProductReviewSummary = withTheme(ProductReviewSummaryComponent);
